import isUndefined from 'lodash-es/isUndefined';
import { fromEvent, Observable } from 'rxjs';
import { filter, first, mergeMap, share, tap } from 'rxjs/operators';
import { hasCodeAncestor } from '../../helpers/dom/code';
import { getTextBeforeCursor } from '../../helpers/textUnderCursor';
import { InteractionTrigger } from '../../interactionEvent';
import { Service } from '../base/service';
import { IndentAsYouTypeServiceOptions } from '../text/indentAsYouTypeService';
import { TextUnderCursorService } from '../text/textUnderCursorService';
import { UndoService } from '../text/undoService';
import { ElementSelectionService } from './elementSelectionService';
import { createElementEffectService } from './elementService';

export function createElementIndentAsYouTypeService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  paragraphTextUnderCursorService: TextUnderCursorService,
  undoService: UndoService,
  options: IndentAsYouTypeServiceOptions,
): Service {
  return createElementEffectService(element$, element =>
    fromEvent<KeyboardEvent>(element, 'keydown').pipe(
      // Listen for the Space key being pressed, to establish typing intent.
      filter(event => event.key === ' '),
      // Listen for the next text/selection change that resulted from the key press.
      mergeMap(() => paragraphTextUnderCursorService.changes$.pipe(first())),
      tap(() => {
        // Make sure we have a simple cursor.
        const { selection } = selectionService;
        if (!selection || !selection.isCollapsed) {
          return;
        }
        if (options.getHasListAutoFormatting && !options.getHasListAutoFormatting()) {
          return;
        }
        // Are we already in inline code or a code block?
        if (hasCodeAncestor(selection.focusNode, element)) {
          return;
        }

        const { textUnderCursor } = paragraphTextUnderCursorService;
        const textBeforeCursor = getTextBeforeCursor(textUnderCursor);

        // Check if the text before the cursor is a marker + a space.
        const markerMatch = textBeforeCursor.match(/^(?<marker>.+)\s$/);
        const marker = markerMatch?.groups?.marker;
        if (isUndefined(marker)) {
          return;
        }

        // Check if the marker is a known indentation marker.
        const markerOptions = options.markers.find(candidateOptions => candidateOptions.marker.test(marker));
        if (!markerOptions) {
          return;
        }

        // Allow the user to undo this.
        undoService.takeAndCommitSnapshot();

        // Remove the text before the cursor.
        const textBeforeCursorLength = textBeforeCursor.length;
        paragraphTextUnderCursorService.setTextUnderCursor({
          text: textUnderCursor.text.slice(textBeforeCursorLength),
          anchorOffset: Math.max(0, textUnderCursor.anchorOffset - textBeforeCursorLength),
          focusOffset: Math.max(0, textUnderCursor.focusOffset - textBeforeCursorLength),
        });

        // Perform the indent.
        markerOptions.indent(marker);

        if (options.onInteraction) {
          options.onInteraction({
            trigger: InteractionTrigger.KEYBOARD_TYPING,
            action: markerOptions.action,
          });
        }
      }),
      share(),
    ),
  );
}
